/**
 * calculateCAE.js
 * Calcul du revenu net avant PAS pour le statut CAE (Entrepreneur·e Salarié·e)
 * Cotisations calculées localement (réduction Fillon) ; le SMIC est une constante annuelle.
 */

// ⚠️ DEV : mettre à true pour utiliser une valeur de SMIC de test au lieu du SMIC 2025
const CAE_DEV_NO_API = false;

/**
 * Calcule la contribution CAE selon le barème dégressif sur la marge brute
 */
function calculateContributionCAE(marge_brute, params) {
  const tranches = params.tranches_contribution;
  const minimale = params.contribution_minimale ?? 200;

  if (!tranches || tranches.length === 0) {
    const taux = (params.contribution_pourcentage ?? 14) / 100;
    return Math.max(Math.max(0, marge_brute) * taux, minimale);
  }

  let contribution = 0;
  let base_restante = Math.max(0, marge_brute);
  let plafond_precedent = 0;

  for (const tranche of tranches) {
    if (tranche.taux === 0 || base_restante <= 0) break;

    const plafond_tranche = tranche.plafond > 0
      ? tranche.plafond - plafond_precedent
      : Infinity;

    const montant_dans_tranche = Math.min(base_restante, plafond_tranche);
    contribution += montant_dans_tranche * tranche.taux;
    base_restante -= montant_dans_tranche;
    if (tranche.plafond > 0) plafond_precedent = tranche.plafond;
  }

  return Math.max(contribution, minimale);
}

/**
 * Coefficient Fillon pour un brut mensuel donné.
 */
function coeffRGDU(brutMensuel, smicAnnuel, tMin, tDelta, tx_caisse_cp) {
  if (brutMensuel <= 0) return 0;
 
  const remuAnnuelle = brutMensuel * 12;
    // Au-delà de 3 SMIC : pas de réduction
  if (remuAnnuelle >= 3 * smicAnnuel) return 0;
 
  const terme = 0.5 * ((3 * smicAnnuel / remuAnnuelle) - 1);
  const coeff = (tMin + tDelta * terme ** 1.75) * tx_caisse_cp; //1.75 =  exposant de dégressivité
 
  // Plancher : Tmin × 100/90 (garanti pour toute rému < 3 SMIC)
  // Plafond  : (Tmin + Tdelta) × 100/90
  return Math.min(Math.max(coeff, tMin * tx_caisse_cp), (tMin + tDelta) * tx_caisse_cp);
}
 


/**
 * Cotisations pour un brut connu.
 */
function cotisationsDepuisBrut(brut, smic, tMin, tDelta, tx_patronales, tx_salariales, tx_caisse_cp) {
  const coefficientRGDU  = coeffRGDU(brut, smic, tMin, tDelta, tx_caisse_cp);
  const reductionRGDU    = coefficientRGDU * brut;
  const patronales   = brut * tx_patronales - reductionRGDU;
  const salariales   = brut * tx_salariales;
  return {
    brut,
    reductionRGDU,
    patronales,
    salariales,
    net:       brut - salariales,
    coutTotal: brut + patronales,
  };
}

/**
 * Calcule le brut à partir d'une marge (CA - matières - contribution CAE).
 * Itération nécessaire car coeffFillon(brut) est non-linéaire (terme ^1.75).
 * Converge en < 10 itérations.
 */
function brutDepuisMarge(marge_eco, smic_annuel, tMin, tDelta, tx_patronales, tx_salariales, tx_caisse_cp) {
  if (marge_eco <= 0) return null;
 
  // Estimation initiale sans réduction
  let brut = marge_eco / (1 + tx_patronales);
 
  for (let i = 0; i < 50; i++) {
    const coefficientRGDU  = coeffRGDU(brut, smic_annuel, tMin, tDelta, tx_caisse_cp);
    const reductionRGDU    = coefficientRGDU * brut;
    const patronales = brut * tx_patronales - reductionRGDU;
    const brutSuivant    = marge_eco - patronales;
 
    if (Math.abs(brutSuivant - brut) < 0.005) break;
    brut = 0.4 * brut + 0.6 * brutSuivant;
  }
 
  return cotisationsDepuisBrut(brut, smic_annuel, tMin, tDelta, tx_patronales, tx_salariales, tx_caisse_cp);
}

/**
 * Calcule le revenu net avant PAS pour le statut CAE
 *
 * @param {number} ca_ht - Chiffre d'affaires HT mensuel
 * @param {object} charges - Objet contenant toutes les charges professionnelles
 * @param {number} charges.matieres_premieres - Matières premières et marchandises (0 = non renseigné)
 * @param {number} charges.sous_traitance - Coût de sous-traitance
 * @param {number} charges.frais_professionnels_total - Total des frais professionnels récupérables
 * @param {object} params_cae - Paramètres spécifiques de la CAE
 * @param {number} params_cae.contribution_pourcentage - % de contribution CAE sur CA HT (ex: 14)
 * @param {number} params_cae.forfait_rcp_cae_batiment - Forfait RCP mensuel bâtiment (ex: 20)
 * @param {number} params_cae.tx_decennale_batiment - Taux RCP bâtiment appliqué au CA HT (ex: 0.03 pour 3%)
 * @param {number} params_cae.forfait_rcp_cae_hors_batiment - Forfait RCP mensuel hors bâtiment (ex: 15)
 * @param {number} params_cae.charges_forfaitaires_mutuelles - Mutuelle mensuelle (ex: 84)
 * @param {number} params_cae.charges_forfaitaires_annuel_medecine_travail - Médecine du travail annuelle (ex: 95)
 * @param {number} params_cae.taux_cotisation_fede_urscop - % cotisation fédé sur salaire brut (ex: 0.42)
 * @param {string} metier - Métier exercé (détermine le calcul RCP et les matières premières par défaut)
 * @returns {Promise<object>} Détails complets du calcul avec tous les intermédiaires
 */
export async function calculateCAE(ca_ht, isTTC, charges, params_cae, metier = '') {
  // Validation des entrées
  if (!ca_ht || ca_ht < 0) {
    throw new Error('Le chiffre d\'affaires doit être un nombre positif');
  }

  if (!charges || typeof charges !== 'object') {
    throw new Error('Les charges doivent être un objet valide');
  }

  if (!params_cae || typeof params_cae !== 'object') {
    throw new Error('Les paramètres CAE doivent être un objet valide');
  }

  const METIER_BATIMENT    = 'Bâtiment, travaux';
  const METIER_ALIMENTAIRE = 'Production alimentaire, culinaire';
  const METIER_FORMATION   = 'Formation professionnelle';
  const METIERS_CAISSE_CP  = ['Bâtiment, travaux'];
  const is_batiment    = metier === METIER_BATIMENT;
  const is_alimentaire = metier === METIER_ALIMENTAIRE;
  const is_formation   = metier === METIER_FORMATION;
  const is_metier_caisse_cp = METIERS_CAISSE_CP.includes(metier);

  // Pour le métier bâtiment, le taux de cotisations patronales spécifique est obligatoire.
  // Si la CAE ne l'a pas configuré, la simulation est impossible.
  if (is_batiment && (params_cae.tx_cotisations_patronales_batiment === null || params_cae.tx_cotisations_patronales_batiment === undefined)) {
    return {
      statut: 'CAE',
      erreur_config: "Le taux de cotisations patronales spécifique au métier bâtiment n'est pas configuré pour cette CAE. La simulation ne peut pas être effectuée. Merci de vous rapprocher de l'administrateur de votre CAE.",
      details: { metier, is_batiment: true },
    };
  }

  const coeff = isTTC ? 1.2 : 1;

  // ========== ÉTAPE 1 : MATIÈRES PREMIÈRES ==========
  const matieres_premieres_saisies = charges.matieres_premieres / coeff || 0;
  let matieres_premieres_pour_calcul;
  let matieres_premieres_est_defaut = false;
  let matieres_premieres_defaut_pct = 0;

  if (matieres_premieres_saisies > 0) {
    matieres_premieres_pour_calcul = matieres_premieres_saisies;
  } else if (is_batiment) {
    matieres_premieres_pour_calcul = ca_ht * 0.45;
    matieres_premieres_est_defaut = true;
    matieres_premieres_defaut_pct = 45;
  } else {
    matieres_premieres_pour_calcul = 0;
  }

  const sous_traitance = charges.sous_traitance / coeff || 0;

  // ========== ÉTAPE 2 : MARGE BRUTE ==========
  const marge_brute = ca_ht - matieres_premieres_pour_calcul - sous_traitance;

  if (marge_brute < 0) {
    console.warn('⚠️ Attention: La marge brute est négative');
  }

  // ========== ÉTAPE 3 : CONTRIBUTION CAE (barème dégressif sur marge brute) ==========
  const contribution_cae = calculateContributionCAE(marge_brute, params_cae);

  // ========== ÉTAPE 4 : FRAIS DE FONCTIONNEMENT (opérationnel, depuis les champs individuels) ==========
  const frais_fonctionnement =
    ((charges.loyer || 0) +
    (charges.internet_telephone || 0) * 0.5 +
    (charges.indemnites_km || 0) +
    (charges.deplacements || 0) +
    (charges.communication_pub || 0) +
    (charges.consommables || 0) +
    (charges.repas_affaires || 0)) / coeff;

  // ========== ÉTAPE 5 : FRAIS SPÉCIFIQUES CAE ==========
  const label_bio_fixe    = params_cae.label_bio_fixe_annuel ?? 160;
  const label_bio_variable = params_cae.label_bio_variable_annuel ?? 180;
  const label_bio_cae = is_alimentaire ? (label_bio_fixe + label_bio_variable) / 12 : 0;

  const tx_logiciel_formation = params_cae.tx_logiciel_formation ?? 0.03;
  const logiciel_formation_cae = is_formation ? ca_ht * tx_logiciel_formation : 0;

  // ========== ÉTAPE 6 : RCP / CGscop ==========
  const forfait_rcp_batiment      = params_cae.forfait_rcp_cae_batiment ?? 20;
  const tx_decennale_batiment     = params_cae.tx_decennale_batiment ?? 0.03;
  const forfait_rcp_hors_batiment = params_cae.forfait_rcp_cae_hors_batiment ?? 15;
  const cgscop_inclu              = params_cae.cgscop_inclu_contribution_cae ?? true;
  const tx_cgscop                 = params_cae.tx_cgscop ?? 0.0048;

  const cgscop_montant = cgscop_inclu ? 0 : ca_ht * tx_cgscop;

  const rcp_cgscop_mensuel = is_batiment
    ? forfait_rcp_batiment + ca_ht * tx_decennale_batiment + cgscop_montant
    : forfait_rcp_hors_batiment + cgscop_montant;

  // ========== ÉTAPE 7 : MARGE ÉCONOMIQUE ==========
  const marge_economique =
    marge_brute
    - contribution_cae
    - rcp_cgscop_mensuel
    - label_bio_cae
    - logiciel_formation_cae
    - frais_fonctionnement;

  let avertissement = null;
  if (marge_economique < 0) {
    console.warn('⚠️ Attention: La marge économique est négative. Aucun salaire ne peut être versé.');
    avertissement = '⚠️ Votre marge économique est négative : le chiffre d’affaires ne couvre pas les charges fixes. Aucun salaire ne peut être versé tant que la marge ne redevient pas positive.';
  }

  // ========== CHARGES FORFAITAIRES CAE (communes aux deux branches) ==========
  const charges_forfaitaires_mutuelles = params_cae.charges_forfaitaires_mutuelles ?? 84;
  const charges_forfaitaires_medecine_travail_annuel = params_cae.charges_forfaitaires_annuel_medecine_travail ?? 94;
  const charges_forfaitaires = charges_forfaitaires_mutuelles + charges_forfaitaires_medecine_travail_annuel / 12;
  const taux_cotisation_fede = params_cae.taux_cotisation_fede_urscop ?? 0.0086;
  const cotisation_fede_urscop = marge_brute * taux_cotisation_fede;

  // ========== ÉTAPE 8 : CALCUL DU SALAIRE (formule avec réduction Fillon) ==========
  let salaire_brut_corrige, salaire_brut_api, charges_patronales, charges_patronales_brutes,
      reduction_fillon, cotisations_salariales, salaire_net_avant_pas, salaire_net_sans_cp;

  // Le SMIC est une constante annuelle : pas d'appel API (évite une requête par calcul).
  // Le calcul CAE est donc 100 % local et toujours fiable.
  const SMIC_MENSUEL = CAE_DEV_NO_API ? 2500 : 1867.02; // SMIC mensuel brut
  const source = CAE_DEV_NO_API ? 'fallback' : 'api';

  const SMIC_ANNUEL = SMIC_MENSUEL * 12;
  const TMIN        = 0.0200;   // plancher d'exonération (2 %)
  const TMAX        = params_cae.cae_moins_50_salaries ?? true
    ? params_cae.tx_max_coef_rgdu_moins_50_salaries ?? 0.3981
    : params_cae.tx_max_coef_rgdu_plus_50_salaries ?? 0.4021;
  const TDELTA      = TMAX - TMIN;

  // Caisse CP : uniquement si affiliée ET métier éligible (bâtiment, travaux publics)
  const tx_caisse_cp = (params_cae.cae_affiliee_caisse_conges_payes && is_metier_caisse_cp) ? (100 / 90) : 1;

  // Taux de cotisations patronales :
  // - métier bâtiment : taux spécifique obligatoire (validé en amont, voir early return)
  // - autres métiers : priorité au taux personnalisé saisi par la CAE, sinon taux moyen selon la taille
  const TAUX_PATRON = is_batiment
    ? params_cae.tx_cotisations_patronales_batiment
    : (params_cae.tx_cotisations_patronales_personnalise
        ?? (params_cae.cae_moins_50_salaries ?? true
              ? params_cae.tx_cotisations_patronales_moyen_moins_50_salaries ?? 0.443
              : params_cae.tx_cotisations_patronales_moyen_plus_50_salaries ?? 0.4515));
  const TAUX_SAL    = params_cae.tx_cotisations_salariales ?? 0.2181;

  // Déduction des charges forfaitaires (mutuelle, médecine du travail) et cotisation fédé
  const marge_pour_salaire = marge_economique - charges_forfaitaires - cotisation_fede_urscop;

  // Calcul du salaire brut par itération (réduction Fillon non-linéaire)
  const brutCalc = brutDepuisMarge(marge_pour_salaire, SMIC_ANNUEL, TMIN, TDELTA, TAUX_PATRON, TAUX_SAL, tx_caisse_cp);

  if (!brutCalc) {
    salaire_brut_corrige      = 0;
    salaire_brut_api          = 0;
    reduction_fillon          = 0;
    charges_patronales        = 0;
    charges_patronales_brutes = 0;
    cotisations_salariales    = 0;
    salaire_net_sans_cp       = 0;
    salaire_net_avant_pas     = 0;
  } else {
    salaire_brut_corrige      = brutCalc.brut;
    salaire_brut_api          = brutCalc.brut;
    reduction_fillon          = brutCalc.reductionRGDU;
    charges_patronales        = brutCalc.patronales;
    charges_patronales_brutes = brutCalc.patronales + brutCalc.reductionRGDU;
    cotisations_salariales    = brutCalc.salariales;
    const cp_estimes          = (params_cae.cae_affiliee_caisse_conges_payes && is_metier_caisse_cp) ? brutCalc.net * 0.1 * 1.3 : 0;
    salaire_net_sans_cp       = brutCalc.net;
    salaire_net_avant_pas     = brutCalc.net + cp_estimes;
  }

  // ========== VALEURS DÉRIVÉES ==========
  const frais_pro_hors_charges_cae = matieres_premieres_pour_calcul + sous_traitance + frais_fonctionnement;
  const indemnites_km = charges.indemnites_km || 0;
  const frais_pro_recuperes = (frais_pro_hors_charges_cae - indemnites_km) * 1.2 + indemnites_km;
  const montant_total_verse = salaire_net_avant_pas + frais_pro_recuperes;

  // ========== ARRONDI À 2 DÉCIMALES ==========
  const arrondir = (nombre) => Math.round(nombre * 100) / 100;

  // Frais de participation coopérative (pour affichage carte)
  const frais_participation_coop_display =
    contribution_cae + rcp_cgscop_mensuel + label_bio_cae + logiciel_formation_cae;

  // ========== RETOUR DÉTAILLÉ ==========
  return {
    source,
    salaire_net_avant_pas: arrondir(salaire_net_avant_pas),
    salaire_net_sans_cp: arrondir(salaire_net_sans_cp),
    montant_total_verse: arrondir(montant_total_verse),

    ca_ht: arrondir(ca_ht),
    marge_brute: arrondir(marge_brute),
    contribution_cae: arrondir(contribution_cae),
    marge_economique: arrondir(marge_economique),
    salaire_brut: arrondir(salaire_brut_corrige),
    salaire_brut_api: arrondir(salaire_brut_api),
    charges_patronales: arrondir(charges_patronales),
    charges_patronales_brutes: arrondir(charges_patronales_brutes),
    reduction_fillon: arrondir(reduction_fillon ?? 0),
    cotisations_salariales: arrondir(cotisations_salariales),
    frais_pro_recuperes: arrondir(frais_pro_recuperes),
    avertissement,

    // 4 catégories pour les cartes résultats
    categories_charges: {
      matieres_et_sous_traitance: arrondir(matieres_premieres_pour_calcul + sous_traitance),
      frais_participation_coop: arrondir(frais_participation_coop_display),
      autres_frais_structure: 0,
      frais_fonctionnement: arrondir(frais_fonctionnement),
    },

    details: {
      matieres_premieres: arrondir(matieres_premieres_saisies),
      matieres_premieres_pour_calcul: arrondir(matieres_premieres_pour_calcul),
      matieres_premieres_est_defaut,
      matieres_premieres_defaut_pct,
      sous_traitance: arrondir(sous_traitance),
      frais_professionnels: arrondir(frais_fonctionnement),
      rcp_cgscop_mensuel: arrondir(rcp_cgscop_mensuel),
      cgscop_montant: arrondir(cgscop_montant),
      cgscop_inclu,
      label_bio_cae: arrondir(label_bio_cae),
      logiciel_formation_cae: arrondir(logiciel_formation_cae),
      charges_forfaitaires: arrondir(charges_forfaitaires),
      charges_forfaitaires_mutuelles: arrondir(charges_forfaitaires_mutuelles),
      charges_forfaitaires_medecine_travail_mensuel: arrondir(charges_forfaitaires_medecine_travail_annuel / 12),
      cotisation_fede_urscop: arrondir(cotisation_fede_urscop),
      taux_cotisation_fede,
      reduction_fillon: arrondir(reduction_fillon ?? 0),
      charges_patronales_brutes: arrondir(charges_patronales_brutes),
      metier,
      rcp_mode: is_batiment ? 'batiment' : 'hors_batiment',
      is_batiment,
    },

    statut: 'CAE',
    description: "Entrepreneur·e Salarié·e en Coopérative d'Activités et d'Emploi",

    protection_sociale: {
      assurance_chomage: true,
      retraite_complete: true,
      accident_travail: true,
      maladie_professionnelle: true,
      formation_professionnelle: true,
    },
  };
}

/**
 * Version simplifiée pour un calcul rapide
 * @param {number} ca_ht - Chiffre d'affaires HT
 * @param {number} charges_totales - Total de toutes les charges
 * @param {object} params_cae - Paramètres CAE (optionnel, utilise les valeurs par défaut)
 * @param {string} metier - Métier exercé (optionnel, détermine le calcul RCP)
 * @returns {Promise<number>} Salaire net avant PAS
 */
export async function calculateCAESimple(ca_ht, charges_totales, params_cae = {}, metier = '') {
  const params = {
    contribution_minimale: 200,
    tranches_contribution: [
      { taux: 0.14, plafond: 45000 },
      { taux: 0.12, plafond: 60000 },
      { taux: 0.04, plafond: 0 },
    ],
    forfait_rcp_cae_batiment: 20,
    tx_decennale_batiment: 0.03,
    forfait_rcp_cae_hors_batiment: 15,
    charges_forfaitaires_mutuelles: 84,
    charges_forfaitaires_annuel_medecine_travail: 94,
    taux_cotisation_fede_urscop: 0.0086,
    cgscop_inclu_contribution_cae: true,
    tx_cgscop: 0.0048,
    label_bio_fixe_annuel: 160,
    label_bio_variable_annuel: 180,
    tx_logiciel_formation: 0.03,
    ...params_cae,
  };

  // charges_totales passé comme frais de fonctionnement simple
  const charges = { matieres_premieres: 0, sous_traitance: 0, loyer: charges_totales };
  const resultat = await calculateCAE(ca_ht, false, charges, params, metier);
  return resultat.salaire_net_avant_pas;
}

export default calculateCAE;
